var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DataPool, degToRad } from "flavr-common";
import { Quaternion, Vector3 } from "flavr-math";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { camera } from "../rendering/camera";
import { Color, Sprite, SpriteMaterial, TextureLoader } from "three";
import { mainScene } from "../rendering/mainScene";
const indicatorTextureMap = new Map();
const textureLoader = new TextureLoader();
class SpritePool extends DataPool {
    createObject() {
        const material = new SpriteMaterial({
            depthTest: false,
            depthWrite: false,
            color: 0xffffff,
        });
        const sprite = new Sprite(material);
        // Pool growth can create more sprites than are immediately acquired (DataPool doubles capacity), leaving
        // spares in the free list. Keep those invisible - createSprite() flips this back to true once a sprite is
        // actually acquired and given a real texture - otherwise unconfigured spares render as plain white squares
        // at the origin.
        sprite.visible = false;
        mainScene.add(sprite);
        return sprite;
    }
    destroyObject(sprite) {
        mainScene.remove(sprite);
        sprite.geometry.dispose();
        sprite.material.dispose();
    }
}
const spritePool = new SpritePool(0);
export class ModelIndicator {
    constructor() {
        this.spritesMap = new Map();
        this.pendingIcons = new Map();
        this.direction = new Vector3();
        this.cameraOrientation = new Quaternion();
        this.destroyed = false;
        this.position = new Vector3();
    }
    update() {
        if (this.spritesMap.size === 0)
            return;
        localPlayerContainer.transformPlatformToWorldOrientation(this.cameraOrientation.copy(camera.perspectiveCam.quaternion));
        const angleStep = 20;
        const radius = 0.2;
        const scale = 0.04;
        let i = 0;
        for (const sprite of this.spritesMap.values()) {
            sprite.position.copy(this.position);
            sprite.scale.setScalar(scale);
            // Offset icon based on camera
            const angle = degToRad(i * angleStep);
            this.direction.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0)
                .applyQuaternion(this.cameraOrientation);
            sprite.position.add(this.direction);
            i++;
        }
    }
    destroy() {
        this.destroyed = true;
        this.pendingIcons.clear();
        this.removeAllSprites();
    }
    setWorldPosition(position) {
        this.position.copy(position);
    }
    /// Icon methods ///
    addIcon(indicator) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.destroyed)
                return;
            const sprite = this.spritesMap.get(indicator.url);
            if (sprite !== undefined) {
                sprite.material.color.set(indicator.color);
                return;
            }
            const pending = this.pendingIcons.get(indicator.url);
            if (pending !== undefined) {
                pending.color = indicator.color;
                return pending.promise;
            }
            const request = { color: indicator.color, promise: Promise.resolve() };
            this.pendingIcons.set(indicator.url, request);
            request.promise = this.loadIcon(indicator.url, request);
            return request.promise;
        });
    }
    removeIcon(indicatorURL) {
        this.pendingIcons.delete(indicatorURL);
        const sprite = this.spritesMap.get(indicatorURL);
        if (sprite === undefined)
            return;
        this.removeSprite(sprite);
        this.spritesMap.delete(indicatorURL);
    }
    /// Private sprite helper methods ///
    loadIcon(url, request) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const texture = yield this.getOrLoadIndicatorTexture(url);
                if (this.destroyed || this.pendingIcons.get(url) !== request)
                    return;
                const sprite = spritePool.acquire();
                sprite.visible = true;
                sprite.material.map = texture;
                sprite.material.color = new Color(request.color);
                sprite.material.needsUpdate = true;
                this.spritesMap.set(url, sprite);
            }
            finally {
                if (this.pendingIcons.get(url) === request) {
                    this.pendingIcons.delete(url);
                }
            }
        });
    }
    removeSprite(sprite) {
        sprite.visible = false;
        sprite.material.map = null;
        sprite.material.dispose();
        spritePool.release(sprite);
    }
    removeAllSprites() {
        this.spritesMap.forEach(sprite => {
            this.removeSprite(sprite);
        });
        this.spritesMap.clear();
    }
    /// Private texture helper ///
    getOrLoadIndicatorTexture(url) {
        return __awaiter(this, void 0, void 0, function* () {
            let texture = indicatorTextureMap.get(url);
            if (texture === undefined) {
                texture = yield textureLoader.loadAsync(url);
                indicatorTextureMap.set(url, texture);
            }
            return texture;
        });
    }
}
